import {
  addYears,
  endOfQuarter,
  endOfYear,
  format,
  getDaysInMonth,
  getQuarter,
  isFriday,
  isValid,
  parseISO,
  previousFriday,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
} from 'date-fns';

export type QuarterValue = 'Q1' | 'Q2' | 'Q3' | 'Q4';

export function toIsoDate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export function toDateParts(isoDate: string): { month: number; day: number } {
  const [, month, day] = isoDate.split('-').map(Number);
  return { month, day };
}

export function getLastPassedFriday(): string {
  const today = new Date();
  return toIsoDate(isFriday(today) ? today : previousFriday(today));
}

export function getStartOfYearDate(): string {
  return toIsoDate(startOfYear(new Date()));
}

export function getEndOfYearDate(year: number): string {
  return toIsoDate(endOfYear(new Date(year, 0, 1)));
}

export function getLastWeekThursday(): string {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  return toIsoDate(subDays(monday, 4));
}

export function getLastWeekFriday(): string {
  const monday = startOfWeek(new Date(), { weekStartsOn: 1 });
  return toIsoDate(subDays(monday, 10));
}

export function shiftIsoDateByYears(isoDate: string, years: number): string {
  return toIsoDate(addYears(parseISO(isoDate), years));
}

export function buildIsoDate(year: number, month: number, day: number): string {
  const monthDate = new Date(year, month - 1, 1);
  return toIsoDate(new Date(year, month - 1, Math.min(day, getDaysInMonth(monthDate))));
}

export function quarterFromIsoDate(isoDate: string): QuarterValue {
  switch (getQuarter(parseISO(isoDate))) {
    case 1:
      return 'Q1';
    case 2:
      return 'Q2';
    case 3:
      return 'Q3';
    default:
      return 'Q4';
  }
}

export function quarterRange(year: number, quarter: QuarterValue): { start: string; end: string } {
  const quarterStartMonth = { Q1: 0, Q2: 3, Q3: 6, Q4: 9 }[quarter];
  const start = startOfQuarter(new Date(year, quarterStartMonth, 1));
  const end = endOfQuarter(start);
  return { start: toIsoDate(start), end: toIsoDate(end) };
}

export function normalizeAsOfDate(value: unknown): string | null {
  if (!value) {
    return null;
  }

  const date = new Date(`${value}`);
  return isValid(date) ? toIsoDate(date) : null;
}